import React, { useEffect } from 'react'
import Header from 'src/components/layout/Header'
import { detectKeyboardUser } from 'src/helpers/layout'
import { Helmet } from 'react-helmet'
import { I18nextContext } from 'gatsby-plugin-react-i18next'
import BottomMenu from 'src/components/layout/bottom-menu'
import SideSectionsNavigation from 'src/components/ui/side-sections-navigation'
/* eslint-disable-next-line import/no-unresolved */
import { globalHistory } from '@reach/router'
import { gsap, ScrollTrigger } from 'gsap/all'
import Lenis from 'lenis'

import SideMenu from 'src/components/layout/side-menu'
import { markInitialLoadComplete } from 'src/helpers/animations'
import { AuthProvider } from 'src/auth'

interface IMetaProps {
  name?: string
  content?: string
}
interface ILayoutProps {
  title: string
  metas: IMetaProps[]
  children: JSX.Element | (JSX.Element | JSX.Element[])[]
  isSideNavHidden?: boolean
  isBottomMenuHidden?: boolean
  showIso?: boolean
  isHome?: boolean
}

const Layout: React.FC<ILayoutProps> = ({
  title,
  metas,
  children,
  isSideNavHidden,
  isBottomMenuHidden,
  showIso = true,
  isHome,
}) => {
  const lngContext = React.useContext(I18nextContext)
  useEffect(() => {
    detectKeyboardUser()

    if (typeof window !== 'undefined') {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual'
      }
      ScrollTrigger.clearScrollMemory()
      // @ts-ignore
      ScrollTrigger.config({ ignoreMobileResize: true })
      if (!window.location.hash) {
        window.scrollTo(0, 0)
      }
    }

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      syncTouch: false,
      touchMultiplier: 1,
    })

    if (typeof window !== 'undefined' && !window.location.hash) {
      lenis.scrollTo(0, { immediate: true })
    }

    // Expose lenis to window for other components (like SideSectionsNavigation)
    // @ts-ignore
    window.lenis = lenis

    // Synchronize Lenis scroll with GSAP ScrollTrigger
    lenis.on('scroll', ScrollTrigger.update)

    // Synchronize RAF execution via GSAP ticker to eliminate inertial momentum locks
    const updateTicker = (time: number) => {
      lenis.raf(time * 1000)
    }
    gsap.ticker.add(updateTicker)
    gsap.ticker.lagSmoothing(0)

    // Global interceptor for anchor links (#)
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const closestLink = target.closest('a')
      if (closestLink) {
        const href = closestLink.getAttribute('href')
        if (!href) return

        // Extract anchor and path
        const [path, anchor] = href.split('#')

        if (anchor) {
          // Check if the link is to the current page (ignoring language prefix if possible, or just checking if path matches current)
          const isCurrentPage = !path || path === window.location.pathname || path === window.location.href

          if (isCurrentPage) {
            const targetElement = document.getElementById(anchor)
            if (targetElement) {
              e.preventDefault()
              lenis.scrollTo(targetElement, {
                duration: 2,
                offset: 0,
              })
            }
          }
        }
      }
    }

    document.addEventListener('click', handleAnchorClick)

    markInitialLoadComplete()

    const refreshTriggers = () => {
      ScrollTrigger.sort()
      ScrollTrigger.refresh()
    }

    const refreshTimer = setTimeout(refreshTriggers, 400)

    let lastWidth = typeof window !== 'undefined' ? window.innerWidth : 0
    let resizeTimer: any = null
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        if (window.innerWidth === lastWidth) return
        lastWidth = window.innerWidth
      }
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        refreshTriggers()
      }, 250)
    }

    const handleBeforeUnload = () => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual'
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('load', handleResize)
      window.addEventListener('resize', handleResize)
      window.addEventListener('beforeunload', handleBeforeUnload)
    }

    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    const unsubscriber = globalHistory.listen(({ location, action }) => {
      if (action === 'PUSH' && !location.hash) {
        ScrollTrigger.clearScrollMemory()
        lenis.scrollTo(0, { immediate: true })
      }
    })

    return () => {
      clearTimeout(refreshTimer)
      clearTimeout(resizeTimer)
      if (typeof window !== 'undefined') {
        window.removeEventListener('load', handleResize)
        window.removeEventListener('resize', handleResize)
        window.removeEventListener('beforeunload', handleBeforeUnload)
      }
      document.removeEventListener('click', handleAnchorClick)
      unsubscriber()
      gsap.ticker.remove(updateTicker)
      // @ts-ignore
      window.lenis = null
      lenis.destroy()
    }
  }, [])

  return (
    <AuthProvider>
      <div className="font-roag font-palanquin whitespace-pre-line bg-white dark:bg-surface-background min-h-screen">
        <Helmet
          htmlAttributes={{ lang: lngContext.language }}
          titleTemplate="MAMF | %s"
        >
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
          <meta name="apple-mobile-web-app-capable" content="yes" />
          <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
          <title>{title}</title>
          {metas.map((meta) => (
            <meta key={meta.name} name={meta.name} content={meta.content} />
          ))}

          <script
            id="Cookiebot"
            src="https://consent.cookiebot.com/uc.js"
            data-cbid="55ad7cf8-686a-4367-ae55-55168372b12f"
            data-blockingmode="auto"
            type="text/javascript"
          ></script>
          <script src="https://cdn.userway.org/widget.js" data-account="Pb04VC8HHE"></script>
        </Helmet>
        {/* Safari top-edge transparency anchor */}
        <div className="fixed top-0 left-0 right-0 h-[env(safe-area-inset-top,20px)] bg-transparent pointer-events-none z-30" aria-hidden="true" />
        <Header showIso={showIso} isHome={isHome} />
        <SideMenu />
        <div className="relative text-black dark:text-white">
          <SideSectionsNavigation
            className={`hidden ${isSideNavHidden ? '' : 'xl:block'}`}
          />
          <main className="main">{children}</main>
        </div>
        {!isBottomMenuHidden && <BottomMenu />}
      </div>
    </AuthProvider>
  )
}

Layout.defaultProps = {
  isSideNavHidden: true,
  isBottomMenuHidden: false,
  showIso: true,
}

export default Layout
